import { useEffect, useRef, useState } from "react";

import { faMessageLines } from "@fortawesome/pro-regular-svg-icons";

import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import {
  CommentBox,
  HistorySingleEntry,
} from "src/cases/caseView/history/HistoryItemFrame";
import { CaseHistoryEvent } from "src/cases/queries/casesTypes";

export const CommentAdded: React.FC<{
  entry: CaseHistoryEvent<"comment_added">;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isContentClamped = useIsContentClamped(contentRef);

  return (
    <HistorySingleEntry
      actorId={entry.event_data.user_id}
      content={
        <CommentBox>
          <div
            ref={contentRef}
            className={expanded ? "line-clamp-none" : "line-clamp-3"}
          >
            <MarkdownRenderer content={entry.event_data.comment_text} />
          </div>

          {isContentClamped && (
            <button
              className="font-inter-medium-12px text-indigo-600"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "See less" : "See more"}
            </button>
          )}
        </CommentBox>
      }
      eventType={entry.event_type}
      headline="A comment was added to the case."
      icon={faMessageLines}
      isLast={isLast}
      timestamp={entry.timestamp}
    />
  );
};

/**
 * Returns true once the content overflows its container.
 * Once detected, the value is latched and never resets to false.
 */
const useIsContentClamped = <T extends HTMLElement>(
  ref: React.RefObject<T | null>,
): boolean => {
  const [isContentClamped, setIsContentClamped] = useState(false);

  useEffect(() => {
    const updateClampedState = () => {
      const el = ref.current;

      if (el && !isContentClamped && el.scrollHeight > el.clientHeight) {
        setIsContentClamped(true);
      }
    };

    updateClampedState();
    window.addEventListener("resize", updateClampedState);

    return () => {
      window.removeEventListener("resize", updateClampedState);
    };
  }, [ref, isContentClamped]);

  return isContentClamped;
};
